// A members list read a page at a time (memberships.list's `next`, list caps H3 and M6): the first page
// for the input as it is now, then each next page added on "Load more". The list shown stays up while a
// new input's first page comes, as a list read in one go did; a page that comes back for an input no
// longer shown is dropped.
import { useCallback, useEffect, useRef, useState } from 'react';
import { client } from '../api.ts';
import { errorText } from '../books/ui.tsx';
import { addRows } from '../blueprint/paged.ts';
import type { Member, MemberCursor, MemberPage } from './model.ts';

export interface MemberPages {
  /** The members read so far, or null before the first page. */
  members: Member[] | null;
  /** The first page's answer: the whole list's `total` and `figures`. */
  first: MemberPage | null;
  error: string | null;
  /** The next page, when there is one for the input shown. */
  more: (() => void) | null;
  /** A next page is being read. */
  busy: boolean;
  reload: () => void;
}

export function useMemberPages(input: Record<string, unknown>): MemberPages {
  const key = JSON.stringify(input);
  const [state, setState] = useState<{
    key: string;
    members: Member[];
    first: MemberPage;
    next: MemberCursor | null;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const shown = useRef(key);
  shown.current = key;
  useEffect(() => {
    let current = true;
    client
      .call<MemberPage>('memberships.list', input)
      .then((p) => {
        if (!current) return;
        setState({ key, members: p.members, first: p, next: p.next });
        setError(null);
      })
      .catch((e: unknown) => current && setError(errorText(e, 'Could not load this.')));
    return () => {
      current = false;
    };
    // the input is compared by value (key)
  }, [key, tick]);
  const next = state?.key === key ? state.next : null;
  const more = useCallback(() => {
    if (!next) return;
    const at = key;
    setBusy(true);
    client
      .call<MemberPage>('memberships.list', { ...input, after: next })
      .then((p) => {
        if (shown.current !== at) return;
        setState((s) =>
          s && s.key === at ? { ...s, members: addRows(s.members, p.members), next: p.next } : s,
        );
        setError(null);
      })
      .catch((e: unknown) => shown.current === at && setError(errorText(e, 'Could not load the rest.')))
      .finally(() => setBusy(false));
  }, [key, next]);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  return {
    members: state?.members ?? null,
    first: state?.first ?? null,
    error,
    more: next ? more : null,
    busy,
    reload,
  };
}

/**
 * A notification's member, on a list a page at a time: read on until its row is in, or the list ends
 * (a renewal notice can be about the 300th to renew).
 */
export function useReadTo(list: MemberPages, id: string | undefined): void {
  const missing = !!id && !!list.members && !list.members.some((m) => m.id === id);
  useEffect(() => {
    if (missing && list.more && !list.busy) list.more();
  }, [missing, list.more, list.busy]);
}
